import React, { useState, useEffect, useCallback } from "react";
import { Compass, CheckCircle2, Play, RotateCcw, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GuidedToursPanel() {
  const [tours, setTours] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const fetchTours = useCallback(async () => {
    const res = await fetch(`${API}/api/tours`, { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      setTours(data.tours || []);
    }
  }, []);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  const startTour = async (tourId) => {
    const res = await fetch(`${API}/api/tours/${tourId}`, { credentials: "include" });
    if (res.ok) {
      const tour = await res.json();
      setActiveTour(tour);
      setCurrentStep(tour.completed_steps || 0);
    }
  };

  const advanceStep = async () => {
    if (!activeTour) return;
    const nextStep = currentStep + 1;
    const completed = nextStep >= activeTour.steps.length;
    await fetch(`${API}/api/tours/${activeTour.id}/progress`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step: nextStep, completed }),
    });
    if (completed) {
      setActiveTour(null);
      setCurrentStep(0);
      fetchTours();
    } else {
      setCurrentStep(nextStep);
    }
  };

  const resetTour = async (tourId) => {
    await fetch(`${API}/api/tours/${tourId}/reset`, { method: "POST", credentials: "include" });
    fetchTours();
  };

  if (!tours.length) return null;

  return (
    <div data-testid="guided-tours-panel">
      {/* Tour List */}
      {!activeTour && (
        <div className="rounded-xl border border-[rgba(255,255,255,0.06)] p-4">
          <h3 className="text-sm font-semibold text-[#C9A84C] mb-3 flex items-center gap-2">
            <Compass size={16} /> Guided Tours
          </h3>
          <div className="space-y-2">
            {tours.map(t => (
              <div key={t.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)]"
                data-testid={`tour-${t.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white">{t.title}</span>
                    {t.completed && <CheckCircle2 size={12} className="text-[#4ADE80]" />}
                  </div>
                  <p className="text-[10px] text-[#555]">{t.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-20 h-1 bg-[#333] rounded-full overflow-hidden">
                      <div className="h-full bg-[#C9A84C] rounded-full" style={{ width: `${t.total_steps > 0 ? (t.completed_steps / t.total_steps * 100) : 0}%` }} />
                    </div>
                    <span className="text-[9px] text-[#555]">{t.completed_steps}/{t.total_steps}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {t.completed ? (
                    <button onClick={() => resetTour(t.id)} className="p-1.5 hover:bg-[rgba(255,255,255,0.06)] rounded" data-testid={`reset-tour-${t.id}`}>
                      <RotateCcw size={14} className="text-[#8B95A5]" />
                    </button>
                  ) : (
                    <button onClick={() => startTour(t.id)} className="p-1.5 hover:bg-[rgba(255,255,255,0.06)] rounded" data-testid={`start-tour-${t.id}`}>
                      <Play size={14} className="text-[#C9A84C]" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Tour Overlay */}
      {activeTour && activeTour.steps[currentStep] && (
        <div className="fixed bottom-6 right-6 w-80 bg-[#1A1D27] border border-[rgba(201,168,76,0.3)] rounded-xl shadow-2xl z-50 p-4" data-testid="active-tour">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#C9A84C] font-semibold">{activeTour.title}</span>
            <span className="text-[10px] text-[#555]">{currentStep + 1}/{activeTour.steps.length}</span>
          </div>
          <h4 className="text-sm font-semibold text-white mb-1">{activeTour.steps[currentStep].title}</h4>
          <p className="text-xs text-[#8B95A5] mb-3">{activeTour.steps[currentStep].content}</p>
          <div className="flex items-center justify-between">
            <button onClick={() => { setActiveTour(null); setCurrentStep(0); }}
              className="text-[10px] text-[#8B95A5] hover:text-white">Skip Tour</button>
            <button onClick={advanceStep}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#C9A84C] text-[#0D0F14] rounded text-xs font-semibold"
              data-testid="next-step-btn">
              {currentStep + 1 >= activeTour.steps.length ? "Finish" : "Next"} <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
